import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Badge } from "./ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Separator } from "./ui/separator";
import { toast } from "sonner";
import {
  Users,
  Building2,
  Plus,
  Trash2,
  Save,
  AlertCircle,
  CheckCircle2,
  Equal,
  Percent,
  Globe,
  FileText,
  Download,
} from "lucide-react";

const territories = [
  { value: "Worldwide", label: "Worldwide" },
  { value: "US", label: "United States" },
  { value: "UK", label: "United Kingdom" },
  { value: "EU", label: "European Union" },
  { value: "CA", label: "Canada" },
  { value: "AU", label: "Australia" },
];

const pros = [
  "ASCAP", "BMI", "SESAC", "GMR", "SOCAN", "Re:Sound", "CMRRA",
  "PRS", "PPL", "GEMA", "SACEM", "SABAM", "AKM", "BUMA", "STIM", "TONO", "KODA", "TEOSTO",
  "SIAE", "SGAE", "SUISA", "ZAIKS", "OSA", "ARTISJUS", "SOZA", "HDS", "AEPI", "RAO",
  "SOKOJ", "UCMR", "LATGA-A", "EAU", "MUSICAUTHOR", "SAS", "UACRR",
  "IMRO", "PPI",
  "APRA", "PPCA", "JASRAC", "KOMCA", "KOSCAP", "MCSC", "CASH", "COMPASS", "MACP",
  "MUST", "MCT", "FILSCAP", "MRCSN", "IPRS",
  "ECAD", "SACM", "SCD", "SAYCO", "ACINPRO", "APDAYC", "AGADU", "SACVEN", "SPAC", "SENAPI",
  "ACUM", "SAMRO",
  "COTT", "ACEMLA",
  "NS", "Other"
];

const SplitEditor = ({ splits: initialSplits, onSave, trackTitle, trackArtist, proData }) => {
  const [territory, setTerritory] = useState("Worldwide");
  const [writerSplits, setWriterSplits] = useState([]);
  const [publisherSplits, setPublisherSplits] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (initialSplits?.length > 0) {
      const writers = initialSplits.filter(s => s.role === "writer");
      const publishers = initialSplits.filter(s => s.role === "publisher" || s.role === "admin");
      setWriterSplits(writers);
      setPublisherSplits(publishers);
      if (initialSplits[0]?.territory) {
        setTerritory(initialSplits[0].territory);
      }
    }
  }, [initialSplits]);

  // Import PRO data into splits with both performing & mechanical shares
  const importProData = () => {
    if (!proData) return;
    
    const newWriters = [];
    const newPublishers = [];
    
    const existingWriterNames = new Set(writerSplits.map(w => w.party?.toUpperCase()));
    const existingPublisherNames = new Set(publisherSplits.map(p => p.party?.toUpperCase()));
    
    const writersToImport = proData.selectedWriters || proData.writers || [];
    const publishersToImport = proData.selectedPublishers || proData.publishers || [];
    
    for (const writer of writersToImport) {
      if (!existingWriterNames.has(writer.name?.toUpperCase())) {
        newWriters.push({
          territory,
          party: writer.name,
          role: "writer",
          represents: [],
          sharePct: 0,
          performing_share: writer.performing_share ?? writer.sharePct ?? 0,
          mechanical_share: writer.mechanical_share ?? 0,
          pro: writer.pro || "",
          ipi: writer.ipi || "",
          authority: writer.authority || "public",
          notes: `Imported from ${writer.pro || 'PRO'}`,
        });
      }
    }
    
    for (const pub of publishersToImport) {
      if (!existingPublisherNames.has(pub.name?.toUpperCase())) {
        newPublishers.push({
          territory,
          party: pub.name,
          role: pub.role || "publisher",
          represents: [],
          sharePct: 0,
          performing_share: pub.performing_share ?? pub.sharePct ?? 0,
          mechanical_share: pub.mechanical_share ?? 0,
          pro: pub.pro || "",
          ipi: pub.ipi || "",
          authority: pub.authority || "public",
          notes: `Imported from ${pub.pro || 'PRO'}`,
        });
      }
    }
    
    if (newWriters.length > 0 || newPublishers.length > 0) {
      setWriterSplits([...writerSplits, ...newWriters]);
      setPublisherSplits([...publisherSplits, ...newPublishers]);
      toast.success(`Imported ${newWriters.length} writers and ${newPublishers.length} publishers`);
    } else {
      toast.info("No new data to import (duplicates skipped)");
    }
  };

  // Totals for performing shares
  const writerPerfTotal = writerSplits.reduce((sum, s) => sum + (parseFloat(s.performing_share) || 0), 0);
  const writerMechTotal = writerSplits.reduce((sum, s) => sum + (parseFloat(s.mechanical_share) || 0), 0);
  const publisherPerfTotal = publisherSplits.reduce((sum, s) => sum + (parseFloat(s.performing_share) || 0), 0);
  const publisherMechTotal = publisherSplits.reduce((sum, s) => sum + (parseFloat(s.mechanical_share) || 0), 0);
  
  const writerPerfComplete = Math.abs(writerPerfTotal - 100) < 0.01;
  const writerMechComplete = Math.abs(writerMechTotal - 100) < 0.01;
  const publisherPerfComplete = Math.abs(publisherPerfTotal - 100) < 0.01;
  const publisherMechComplete = Math.abs(publisherMechTotal - 100) < 0.01;

  const writerIncomplete = writerSplits.length > 0 && (!writerPerfComplete || !writerMechComplete);
  const publisherIncomplete = publisherSplits.length > 0 && (!publisherPerfComplete || !publisherMechComplete);
  const hasIncomplete = writerIncomplete || publisherIncomplete;

  const addWriterSplit = () => {
    setWriterSplits([
      ...writerSplits,
      {
        territory,
        party: "",
        role: "writer",
        represents: [],
        sharePct: 0,
        performing_share: 0,
        mechanical_share: 0,
        pro: "",
        authority: "manual",
        notes: "",
      },
    ]);
  };

  const addPublisherSplit = () => {
    setPublisherSplits([
      ...publisherSplits,
      {
        territory,
        party: "",
        role: "publisher",
        represents: [],
        sharePct: 0,
        performing_share: 0,
        mechanical_share: 0,
        pro: "",
        authority: "manual",
        notes: "",
      },
    ]);
  };

  const updateWriterSplit = (index, field, value) => {
    const updated = [...writerSplits];
    updated[index] = { ...updated[index], [field]: value, territory };
    setWriterSplits(updated);
  };

  const updatePublisherSplit = (index, field, value) => {
    const updated = [...publisherSplits];
    updated[index] = { ...updated[index], [field]: value, territory };
    setPublisherSplits(updated);
  };

  const removeWriterSplit = (index) => {
    setWriterSplits(writerSplits.filter((_, i) => i !== index));
  };

  const removePublisherSplit = (index) => {
    setPublisherSplits(publisherSplits.filter((_, i) => i !== index));
  };

  const splitEqually = (type, shareType) => {
    if (type === "writer" && writerSplits.length > 0) {
      const equalShare = parseFloat((100 / writerSplits.length).toFixed(2));
      setWriterSplits(writerSplits.map(s => ({ ...s, [shareType]: equalShare })));
    }
    if (type === "publisher" && publisherSplits.length > 0) {
      const equalShare = parseFloat((100 / publisherSplits.length).toFixed(2));
      setPublisherSplits(publisherSplits.map(s => ({ ...s, [shareType]: equalShare })));
    }
  };

  const normalizeToHundred = (type, shareType) => {
    const splits = type === "writer" ? writerSplits : publisherSplits;
    const setter = type === "writer" ? setWriterSplits : setPublisherSplits;
    const total = splits.reduce((sum, s) => sum + (parseFloat(s[shareType]) || 0), 0);
    if (splits.length > 0 && total > 0) {
      const factor = 100 / total;
      setter(splits.map(s => ({
        ...s,
        [shareType]: parseFloat(((parseFloat(s[shareType]) || 0) * factor).toFixed(2))
      })));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const allSplits = [...writerSplits, ...publisherSplits].map(s => ({
        ...s,
        territory,
        // Keep sharePct in sync with performing_share for backward compat
        sharePct: parseFloat(s.performing_share) || 0,
        performing_share: parseFloat(s.performing_share) || 0,
        mechanical_share: parseFloat(s.mechanical_share) || 0,
        lastEditedAt: new Date().toISOString(),
        lastEditedBy: "admin",
      }));
      await onSave(allSplits);
      
      if (hasIncomplete) {
        toast.success("Splits saved! Note: Some percentages don't total 100% yet.");
      } else {
        toast.success("Splits saved successfully!");
      }
    } catch (error) {
      toast.error("Failed to save splits");
    } finally {
      setSaving(false);
    }
  };

  const importWritersCount = proData?.selectedWriters?.length || proData?.writers?.length || 0;
  const importPublishersCount = proData?.selectedPublishers?.length || proData?.publishers?.length || 0;
  const hasSelectedData = proData && (proData.selectedWriters?.length > 0 || proData.selectedPublishers?.length > 0);
  const hasProData = proData && (proData.writers?.length > 0 || proData.publishers?.length > 0 || hasSelectedData);

  // Shared percentage header row
  const ShareTotalsBar = ({ perfTotal, mechTotal, perfComplete, mechComplete, type, count }) => (
    <div className="flex items-center justify-between flex-wrap gap-3">
      <div className="flex items-center gap-5">
        <p className="text-sm text-syncchain-text-secondary">
          Perf: <span className={perfComplete ? "text-blue-400 font-medium" : count > 0 ? "text-syncchain-warning" : "text-syncchain-text-muted"}>{perfTotal.toFixed(2)}%</span>
        </p>
        <p className="text-sm text-syncchain-text-secondary">
          Mech: <span className={mechComplete ? "text-purple-400 font-medium" : count > 0 ? "text-syncchain-warning" : "text-syncchain-text-muted"}>{mechTotal.toFixed(2)}%</span>
        </p>
        <div className="flex items-center gap-1">
          <Button variant="ghost" size="sm" onClick={() => splitEqually(type, "performing_share")} disabled={count === 0} className="text-syncchain-text-secondary hover:text-syncchain-text text-xs h-7 px-2">
            <Equal className="w-3 h-3 mr-1" />Perf =
          </Button>
          <Button variant="ghost" size="sm" onClick={() => splitEqually(type, "mechanical_share")} disabled={count === 0} className="text-syncchain-text-secondary hover:text-syncchain-text text-xs h-7 px-2">
            <Equal className="w-3 h-3 mr-1" />Mech =
          </Button>
          <Button variant="ghost" size="sm" onClick={() => normalizeToHundred(type, "performing_share")} disabled={count === 0} className="text-syncchain-text-secondary hover:text-syncchain-text text-xs h-7 px-2">
            <Percent className="w-3 h-3 mr-1" />Perf
          </Button>
          <Button variant="ghost" size="sm" onClick={() => normalizeToHundred(type, "mechanical_share")} disabled={count === 0} className="text-syncchain-text-secondary hover:text-syncchain-text text-xs h-7 px-2">
            <Percent className="w-3 h-3 mr-1" />Mech
          </Button>
        </div>
      </div>
      <Button
        onClick={type === "writer" ? addWriterSplit : addPublisherSplit}
        size="sm"
        className="bg-syncchain-accent/10 text-syncchain-accent hover:bg-syncchain-accent/20"
        data-testid={`add-${type}-btn`}
      >
        <Plus className="w-4 h-4 mr-1" />
        Add {type === "writer" ? "Writer" : "Publisher"}
      </Button>
    </div>
  );

  return (
    <Card className="bg-syncchain-paper border-white/10" data-testid="split-editor">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <CardTitle className="font-heading text-lg text-syncchain-text flex items-center gap-2">
            <Users className="w-5 h-5 text-syncchain-accent" />
            Split Editor
          </CardTitle>
          <div className="flex items-center gap-3 flex-wrap">
            {hasProData && (
              <Button
                onClick={importProData}
                size="sm"
                className={`border ${hasSelectedData ? 'bg-syncchain-accent text-white hover:bg-syncchain-accent-hover border-syncchain-accent' : 'bg-syncchain-success/10 text-syncchain-success hover:bg-syncchain-success/20 border-syncchain-success/30'}`}
                data-testid="import-pro-data-btn"
              >
                <Download className="w-4 h-4 mr-1" />
                {hasSelectedData 
                  ? `Import Selected (${importWritersCount + importPublishersCount})`
                  : `Import All (${importWritersCount + importPublishersCount})`
                }
              </Button>
            )}
            <div className="flex items-center gap-2">
              <Globe className="w-4 h-4 text-syncchain-text-muted" />
              <Select value={territory} onValueChange={setTerritory}>
                <SelectTrigger className="w-40 h-9 bg-syncchain-surface border-white/10 text-syncchain-text">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-syncchain-paper border-white/10">
                  {territories.map(t => (
                    <SelectItem key={t.value} value={t.value} className="text-syncchain-text hover:bg-white/5">
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Badge variant="outline" className={!hasIncomplete ? "bg-syncchain-success/10 text-syncchain-success border-syncchain-success/20" : "bg-syncchain-warning/10 text-syncchain-warning border-syncchain-warning/20"}>
              {!hasIncomplete ? <CheckCircle2 className="w-3 h-3 mr-1" /> : <AlertCircle className="w-3 h-3 mr-1" />}
              {!hasIncomplete ? "Complete" : "Incomplete %"}
            </Badge>
          </div>
        </div>
        {trackTitle && (
          <p className="text-sm text-syncchain-text-secondary mt-2">
            {trackTitle} — {trackArtist}
          </p>
        )}
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="writers" className="w-full">
          <TabsList className="bg-syncchain-surface mb-6">
            <TabsTrigger value="writers" className="data-[state=active]:bg-syncchain-accent data-[state=active]:text-white">
              <Users className="w-4 h-4 mr-2" />
              Writers ({writerSplits.length})
            </TabsTrigger>
            <TabsTrigger value="publishers" className="data-[state=active]:bg-syncchain-accent data-[state=active]:text-white">
              <Building2 className="w-4 h-4 mr-2" />
              Publishers ({publisherSplits.length})
            </TabsTrigger>
            <TabsTrigger value="summary" className="data-[state=active]:bg-syncchain-accent data-[state=active]:text-white">
              <FileText className="w-4 h-4 mr-2" />
              Summary
            </TabsTrigger>
          </TabsList>

          {/* Writers Tab */}
          <TabsContent value="writers" className="space-y-4">
            <ShareTotalsBar
              perfTotal={writerPerfTotal}
              mechTotal={writerMechTotal}
              perfComplete={writerPerfComplete}
              mechComplete={writerMechComplete}
              type="writer"
              count={writerSplits.length}
            />

            {writerSplits.length > 0 ? (
              <div className="space-y-3">
                {/* Column headers */}
                <div className="grid grid-cols-12 gap-3 px-4 text-xs font-mono uppercase tracking-wider text-syncchain-text-muted">
                  <div className="col-span-4">Name</div>
                  <div className="col-span-2">IPI</div>
                  <div className="col-span-2 text-blue-400">Perf %</div>
                  <div className="col-span-2 text-purple-400">Mech %</div>
                  <div className="col-span-2"></div>
                </div>
                {writerSplits.map((split, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-3 items-center p-4 bg-syncchain-surface/50 rounded-lg border border-white/5" data-testid={`writer-split-${idx}`}>
                    <div className="col-span-4">
                      <Input
                        value={split.party}
                        onChange={(e) => updateWriterSplit(idx, "party", e.target.value)}
                        placeholder="Writer name"
                        className="bg-syncchain-surface border-white/10 text-syncchain-text h-9"
                        data-testid={`writer-name-${idx}`}
                      />
                    </div>
                    <div className="col-span-2">
                      <Input
                        value={split.ipi || ""}
                        onChange={(e) => updateWriterSplit(idx, "ipi", e.target.value)}
                        placeholder="IPI #"
                        className="bg-syncchain-surface border-white/10 text-syncchain-text h-9 font-mono"
                        data-testid={`writer-ipi-${idx}`}
                      />
                    </div>
                    <div className="col-span-2">
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={split.performing_share ?? 0}
                        onChange={(e) => updateWriterSplit(idx, "performing_share", parseFloat(e.target.value) || 0)}
                        className="bg-syncchain-surface border-blue-500/20 text-syncchain-text h-9 font-mono focus:border-blue-500/50"
                        data-testid={`writer-perf-input-${idx}`}
                      />
                    </div>
                    <div className="col-span-2">
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={split.mechanical_share ?? 0}
                        onChange={(e) => updateWriterSplit(idx, "mechanical_share", parseFloat(e.target.value) || 0)}
                        className="bg-syncchain-surface border-purple-500/20 text-syncchain-text h-9 font-mono focus:border-purple-500/50"
                        data-testid={`writer-mech-input-${idx}`}
                      />
                    </div>
                    <div className="col-span-2 flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeWriterSplit(idx)}
                        className="text-syncchain-text-muted hover:text-syncchain-error h-9 w-9"
                        data-testid={`writer-remove-${idx}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-syncchain-text-muted">
                <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No writer splits defined</p>
                <p className="text-xs mt-1">Click "Add Writer" to add split information</p>
              </div>
            )}
          </TabsContent>

          {/* Publishers Tab */}
          <TabsContent value="publishers" className="space-y-4">
            <ShareTotalsBar
              perfTotal={publisherPerfTotal}
              mechTotal={publisherMechTotal}
              perfComplete={publisherPerfComplete}
              mechComplete={publisherMechComplete}
              type="publisher"
              count={publisherSplits.length}
            />

            {publisherSplits.length > 0 ? (
              <div className="space-y-3">
                {/* Column headers */}
                <div className="grid grid-cols-12 gap-3 px-4 text-xs font-mono uppercase tracking-wider text-syncchain-text-muted">
                  <div className="col-span-3">Name</div>
                  <div className="col-span-2">IPI</div>
                  <div className="col-span-1">Role</div>
                  <div className="col-span-2 text-blue-400">Perf %</div>
                  <div className="col-span-2 text-purple-400">Mech %</div>
                  <div className="col-span-2"></div>
                </div>
                {publisherSplits.map((split, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-3 items-center p-4 bg-syncchain-surface/50 rounded-lg border border-white/5" data-testid={`publisher-split-${idx}`}>
                    <div className="col-span-3">
                      <Input
                        value={split.party}
                        onChange={(e) => updatePublisherSplit(idx, "party", e.target.value)}
                        placeholder="Publisher name"
                        className="bg-syncchain-surface border-white/10 text-syncchain-text h-9"
                        data-testid={`publisher-name-${idx}`}
                      />
                    </div>
                    <div className="col-span-2">
                      <Input
                        value={split.ipi || ""}
                        onChange={(e) => updatePublisherSplit(idx, "ipi", e.target.value)}
                        placeholder="IPI #"
                        className="bg-syncchain-surface border-white/10 text-syncchain-text h-9 font-mono"
                        data-testid={`publisher-ipi-${idx}`}
                      />
                    </div>
                    <div className="col-span-1">
                      <Select value={split.role} onValueChange={(v) => updatePublisherSplit(idx, "role", v)}>
                        <SelectTrigger className="bg-syncchain-surface border-white/10 text-syncchain-text h-9 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-syncchain-paper border-white/10">
                          <SelectItem value="publisher" className="text-syncchain-text hover:bg-white/5">Pub</SelectItem>
                          <SelectItem value="admin" className="text-syncchain-text hover:bg-white/5">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-2">
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={split.performing_share ?? 0}
                        onChange={(e) => updatePublisherSplit(idx, "performing_share", parseFloat(e.target.value) || 0)}
                        className="bg-syncchain-surface border-blue-500/20 text-syncchain-text h-9 font-mono focus:border-blue-500/50"
                        data-testid={`publisher-perf-input-${idx}`}
                      />
                    </div>
                    <div className="col-span-2">
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        step="0.01"
                        value={split.mechanical_share ?? 0}
                        onChange={(e) => updatePublisherSplit(idx, "mechanical_share", parseFloat(e.target.value) || 0)}
                        className="bg-syncchain-surface border-purple-500/20 text-syncchain-text h-9 font-mono focus:border-purple-500/50"
                        data-testid={`publisher-mech-input-${idx}`}
                      />
                    </div>
                    <div className="col-span-2 flex justify-end">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removePublisherSplit(idx)}
                        className="text-syncchain-text-muted hover:text-syncchain-error h-9 w-9"
                        data-testid={`publisher-remove-${idx}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-syncchain-text-muted">
                <Building2 className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No publisher splits defined</p>
                <p className="text-xs mt-1">Click "Add Publisher" to add split information</p>
              </div>
            )}
          </TabsContent>

          {/* Summary Tab */}
          <TabsContent value="summary" className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-4 bg-syncchain-surface/50 rounded-lg border border-white/5">
                <h4 className="text-xs font-mono uppercase tracking-wider text-syncchain-text-muted mb-3">
                  Writer Splits
                </h4>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-syncchain-text-secondary text-sm">Performing</span>
                    <span className={`font-mono text-sm ${writerPerfComplete ? "text-blue-400" : writerSplits.length > 0 ? "text-syncchain-warning" : "text-syncchain-text-muted"}`}>
                      {writerPerfTotal.toFixed(2)}%
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-syncchain-text-secondary text-sm">Mechanical</span>
                    <span className={`font-mono text-sm ${writerMechComplete ? "text-purple-400" : writerSplits.length > 0 ? "text-syncchain-warning" : "text-syncchain-text-muted"}`}>
                      {writerMechTotal.toFixed(2)}%
                    </span>
                  </div>
                </div>
                <div className={`flex items-center gap-2 mt-3 ${(!writerIncomplete && writerSplits.length > 0) ? "text-syncchain-success" : writerIncomplete ? "text-syncchain-warning" : "text-syncchain-text-muted"}`}>
                  {(!writerIncomplete && writerSplits.length > 0) ? <CheckCircle2 className="w-4 h-4" /> : writerIncomplete ? <AlertCircle className="w-4 h-4" /> : null}
                  <span className="text-sm">{(!writerIncomplete && writerSplits.length > 0) ? "Complete (100%)" : writerIncomplete ? "Incomplete - adjust later" : "No writers added"}</span>
                </div>
              </div>

              <div className="p-4 bg-syncchain-surface/50 rounded-lg border border-white/5">
                <h4 className="text-xs font-mono uppercase tracking-wider text-syncchain-text-muted mb-3">
                  Publisher Splits
                </h4>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-syncchain-text-secondary text-sm">Performing</span>
                    <span className={`font-mono text-sm ${publisherPerfComplete ? "text-blue-400" : publisherSplits.length > 0 ? "text-syncchain-warning" : "text-syncchain-text-muted"}`}>
                      {publisherPerfTotal.toFixed(2)}%
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-syncchain-text-secondary text-sm">Mechanical</span>
                    <span className={`font-mono text-sm ${publisherMechComplete ? "text-purple-400" : publisherSplits.length > 0 ? "text-syncchain-warning" : "text-syncchain-text-muted"}`}>
                      {publisherMechTotal.toFixed(2)}%
                    </span>
                  </div>
                </div>
                <div className={`flex items-center gap-2 mt-3 ${(!publisherIncomplete && publisherSplits.length > 0) ? "text-syncchain-success" : publisherIncomplete ? "text-syncchain-warning" : "text-syncchain-text-muted"}`}>
                  {(!publisherIncomplete && publisherSplits.length > 0) ? <CheckCircle2 className="w-4 h-4" /> : publisherIncomplete ? <AlertCircle className="w-4 h-4" /> : null}
                  <span className="text-sm">{(!publisherIncomplete && publisherSplits.length > 0) ? "Complete (100%)" : publisherIncomplete ? "Incomplete - adjust later" : "No publishers added"}</span>
                </div>
              </div>
            </div>

            <Separator className="bg-white/10" />

            {/* Combined Totals - 200% logic */}
            {(writerSplits.length > 0 || publisherSplits.length > 0) && (
              <>
                <div className="p-4 bg-syncchain-surface/50 rounded-lg border border-white/5">
                  <h4 className="text-xs font-mono uppercase tracking-wider text-syncchain-text-muted mb-3">
                    Combined Totals
                  </h4>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <p className="text-xs text-syncchain-text-muted">Total Performing</p>
                      <p className={`font-mono text-sm font-medium ${Math.abs((writerPerfTotal + publisherPerfTotal) - 100) < 1 ? "text-blue-400" : "text-syncchain-warning"}`}>
                        {(writerPerfTotal + publisherPerfTotal).toFixed(2)}%
                        <span className="text-xs text-syncchain-text-muted ml-1">/ 100%</span>
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-syncchain-text-muted">Total Mechanical</p>
                      <p className={`font-mono text-sm font-medium ${Math.abs((writerMechTotal + publisherMechTotal) - 100) < 1 ? "text-purple-400" : "text-syncchain-warning"}`}>
                        {(writerMechTotal + publisherMechTotal).toFixed(2)}%
                        <span className="text-xs text-syncchain-text-muted ml-1">/ 100%</span>
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-syncchain-text-muted">Combined</p>
                      <p className={`font-mono text-sm font-medium ${Math.abs((writerPerfTotal + publisherPerfTotal + writerMechTotal + publisherMechTotal) - 200) < 2 ? "text-syncchain-success" : "text-syncchain-warning"}`}>
                        {(writerPerfTotal + publisherPerfTotal + writerMechTotal + publisherMechTotal).toFixed(2)}%
                        <span className="text-xs text-syncchain-text-muted ml-1">/ 200%</span>
                      </p>
                    </div>
                  </div>
                </div>
                <Separator className="bg-white/10" />
              </>
            )}

            <div className="p-4 bg-syncchain-surface/50 rounded-lg border border-white/5">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-medium text-syncchain-text">Territory</h4>
                  <p className="text-xs text-syncchain-text-muted mt-1">
                    {territories.find(t => t.value === territory)?.label}
                  </p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-syncchain-text">Authority</h4>
                  <Badge variant="outline" className="bg-white/5 border-white/10 text-xs mt-1">
                    Manual
                  </Badge>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-syncchain-text">Status</h4>
                  <Badge
                    variant="outline"
                    className={`mt-1 ${!hasIncomplete ? "bg-syncchain-success/10 text-syncchain-success border-syncchain-success/20" : "bg-syncchain-warning/10 text-syncchain-warning border-syncchain-warning/20"}`}
                  >
                    {!hasIncomplete ? "Complete" : "Incomplete %"}
                  </Badge>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <Separator className="bg-white/10 my-6" />

        <div className="flex justify-end gap-3">
          {hasIncomplete && (
            <p className="text-xs text-syncchain-warning self-center">
              Some share totals don't equal 100% - you can adjust later
            </p>
          )}
          <Button
            onClick={handleSave}
            disabled={saving || (writerSplits.length === 0 && publisherSplits.length === 0)}
            className="bg-syncchain-accent hover:bg-syncchain-accent-hover text-white px-8 rounded-full glow-hover"
            data-testid="save-splits-btn"
          >
            {saving ? (
              <>
                <span className="w-4 h-4 mr-2 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Splits
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default SplitEditor;
